/**
 * NA-437: "Copy to another lesson" modal. Emtrain-only — the toolbar action
 * that opens this is itself gated to Standard context (ContentCanvas.tsx), so
 * this component doesn't re-check client context.
 *
 * Styled to exactly match the approved prototype
 * (https://copy-block-to-lesson.netlify.app/, `.modal`/`.m-head`/`.m-body`/
 * `.field`/`.m-foot`/`.btn` rules) — literal hex values below are lifted
 * directly from that prototype's stylesheet, not this app's shared design
 * tokens, since the brief was an exact visual match for this one feature
 * rather than this app's own look. `ModalShell` itself (and its backdrop,
 * shared by every other modal in the app) is untouched — only `width`/
 * `borderRadius` are passed in, both already exposed as props for exactly
 * this kind of per-modal customization.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { CourseBlock } from '@course-authoring/shared-types';
import { copyLessonBlockToLesson } from '../api/lessonBlockApi';
import type { LessonListResponseItem } from '../api/courseRecordApi';
import { getBlockDisplayTitle } from '../utils/blockDisplay';
import { formatBlockHumanId } from '../utils/humanIds';
import { LessonCopyCombobox } from './LessonCopyCombobox';
import { ModalShell } from './ModalShell';

type Placement = 'beginning' | 'end';

export interface CopyBlockToLessonResult {
  destinationLessonId: string;
  destinationLessonTitle: string;
  destinationNumericId: number;
  blockId: string;
  openAfterCopying: boolean;
}

export interface CopyBlockToLessonModalProps {
  isOpen: boolean;
  sourceBlock: CourseBlock;
  currentLessonId: string;
  onClose: () => void;
  onCopied: (result: CopyBlockToLessonResult) => void;
}

export function CopyBlockToLessonModal({
  isOpen,
  sourceBlock,
  currentLessonId,
  onClose,
  onCopied,
}: CopyBlockToLessonModalProps) {
  const styles = useMemo(() => createStyles(), []);
  const titleId = useId();

  const [selectedLesson, setSelectedLesson] = useState<LessonListResponseItem | null>(null);
  const [placement, setPlacement] = useState<Placement>('end');
  const [openAfterCopying, setOpenAfterCopying] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Each opening starts clean — a stale selection from a previous copy
  // shouldn't silently carry over to a different source block.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedLesson(null);
    setPlacement('end');
    setOpenAfterCopying(false);
    setError(null);
  }, [isOpen]);

  const handleCopy = async () => {
    if (!selectedLesson) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await copyLessonBlockToLesson(selectedLesson.id, {
        sourceBlockId: sourceBlock.id,
        block: sourceBlock,
        placement,
      });
      onCopied({
        destinationLessonId: selectedLesson.id,
        destinationLessonTitle: selectedLesson.title,
        destinationNumericId: selectedLesson.numericId,
        blockId: response.blockId,
        openAfterCopying,
      });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Failed to copy block');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ModalShell isOpen={isOpen} onClose={onClose} ariaLabelledBy={titleId} width="520px" borderRadius="12px">
      <div style={styles.head}>
        <h3 id={titleId} style={styles.title}>Copy block to another lesson</h3>
        <p style={styles.sub}>
          Copying <b style={styles.subStrong}>{getBlockDisplayTitle(sourceBlock)}</b>
          {formatBlockHumanId(sourceBlock.id) ? ` · ${formatBlockHumanId(sourceBlock.id)}` : ''}
        </p>
      </div>

      <div style={styles.body}>
        <div style={styles.field}>
          <label style={styles.flabel}>Copy to lesson</label>
          <LessonCopyCombobox
            currentLessonId={currentLessonId}
            selected={selectedLesson}
            onSelect={setSelectedLesson}
          />
        </div>
        <p style={styles.hint}>Published lessons can&apos;t receive blocks. Unlock one to make it available.</p>

        <div style={styles.field}>
          <label style={styles.flabel}>Place the copy</label>
          <select
            value={placement}
            onChange={(event) => setPlacement(event.target.value as Placement)}
            style={styles.select}
          >
            <option value="end">At the end of the lesson</option>
            <option value="beginning">At the beginning of the lesson</option>
          </select>
        </div>

        <label style={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={openAfterCopying}
            onChange={(event) => setOpenAfterCopying(event.target.checked)}
          />
          Open that lesson after copying
        </label>

        {error && <p role="alert" style={styles.error}>{error}</p>}
      </div>

      <div style={styles.foot}>
        <div style={styles.spacer} />
        <button
          type="button"
          onClick={onClose}
          style={styles.btn}
          disabled={isSubmitting}
          onMouseEnter={(event) => { if (!isSubmitting) event.currentTarget.style.backgroundColor = '#f4f5f6'; }}
          onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = '#ffffff'; }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => { void handleCopy(); }}
          style={{
            ...styles.btn,
            ...styles.btnPrimary,
            ...(!selectedLesson || isSubmitting ? styles.btnDisabled : {}),
          }}
          disabled={!selectedLesson || isSubmitting}
          aria-busy={isSubmitting}
          onMouseEnter={(event) => {
            if (selectedLesson && !isSubmitting) event.currentTarget.style.backgroundColor = '#1a44bd';
          }}
          onMouseLeave={(event) => { event.currentTarget.style.backgroundColor = '#1f4fd8'; }}
        >
          {isSubmitting ? 'Copying…' : 'Copy block'}
        </button>
      </div>
    </ModalShell>
  );
}

function createStyles(): Record<string, CSSProperties> {
  return {
    head: {
      padding: '18px 20px 14px',
      borderBottom: '1px solid #eceef1',
    },
    title: {
      margin: '0 0 4px 0',
      fontSize: '16px',
      fontWeight: 650,
      letterSpacing: '-0.01em',
      color: '#1f2933',
    },
    sub: {
      margin: 0,
      fontSize: '13px',
      color: '#6b7480',
      lineHeight: 1.5,
    },
    subStrong: {
      fontWeight: 600,
      color: '#1f2933',
    },
    body: {
      padding: '16px 20px',
      overflowY: 'auto',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    field: {
      marginBottom: '16px',
    },
    flabel: {
      display: 'block',
      fontSize: '12px',
      fontWeight: 650,
      letterSpacing: '0.03em',
      textTransform: 'uppercase',
      color: '#6b7480',
      marginBottom: '6px',
    },
    hint: {
      margin: '-10px 0 16px 0',
      fontSize: '12.5px',
      color: '#6b7480',
      lineHeight: 1.4,
    },
    select: {
      width: '100%',
      fontSize: '14px',
      color: '#1f2933',
      backgroundColor: '#ffffff',
      border: '1px solid #dfe3e8',
      borderRadius: '6px',
      padding: '8px 10px',
      boxSizing: 'border-box',
      fontFamily: 'inherit',
    },
    checkboxRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontSize: '14px',
      color: '#1f2933',
      cursor: 'pointer',
    },
    error: {
      margin: '16px 0 0 0',
      fontSize: '13px',
      color: '#c8342b',
      lineHeight: 1.5,
    },
    foot: {
      padding: '14px 20px',
      borderTop: '1px solid #eceef1',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    spacer: {
      flex: 1,
    },
    btn: {
      font: 'inherit',
      fontSize: '14px',
      padding: '8px 16px',
      borderRadius: '6px',
      cursor: 'pointer',
      border: '1px solid #dfe3e8',
      backgroundColor: '#ffffff',
      color: '#1f2933',
    },
    btnPrimary: {
      backgroundColor: '#1f4fd8',
      borderColor: '#1f4fd8',
      color: '#ffffff',
      fontWeight: 550,
    },
    btnDisabled: {
      opacity: 0.45,
      cursor: 'default',
    },
  };
}
